import {observer} from 'mobx-react-lite'
import * as React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {backgroundImages} from '@client/background-images'
import {FilePreview} from '@common/file-preview'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {
  createUseStore,
  textCursorSelectAll,
  useBoard,
  useMemoOne
} from '@containers/hooks'
import {Checkbox} from '../../common/base/checkbox'
import {RadioButton} from '../../common/base/radiobutton'
import {DivWithHint} from '../../common/hint/hint'
import {SharePopupStore} from './share-popup-store'

const [Context, useStore] = createUseStore(SharePopupStore)

const bem = Util.bemFrom('share-popup')

export const ShareInner = observer(() => {
  const store = useMemoOne(() => new SharePopupStore(), [])
  const sharedBoard = store.getSharedBoard()
  const board = useBoard()

  React.useEffect(() => {
    store.setBoard(board.id)
  }, [])

  return (
    <WithPerfectScrollbar className="h-full">
      <div className="ml-8">
        <div className={bem('turn-on')} onClick={() => store.toggle()}>
          <Checkbox value={!!sharedBoard} text={Lang.msg.share.turnOn} />
        </div>
        {sharedBoard ? (
          <Context.Provider value={store}>
            <Link />
            <AccessSettings />
            <BgSettings />
            <OtherSettings />
          </Context.Provider>
        ) : (
          <div className={bem('help')}>{Lang.msg.share.help}</div>
        )}
      </div>
    </WithPerfectScrollbar>
  )
})

const Link = observer(() => {
  const [, store] = useStore()
  const link = store.getLink()
  return (
    <div className={bem('link')}>
      <div className={bem('link-title')}>{Lang.msg.share.publicLink}:</div>
      <div className={bem('url')}>
        <input
          className={bem('url-input')}
          type="text"
          onFocus={textCursorSelectAll}
          value={link}
          onChange={Util.noop}
        />
        <div className={bem('copy')} onClick={() => store.copy()} />
      </div>
    </div>
  )
})

const TwRow = tw.div`
  select-none
  flex
  items-center
  mx-4
  my-8
  cursor-default
`
const TwLabel = tw.div`
  mx-4
  cursor-pointer
  overflow-hidden
  text-ellipsis
  whitespace-nowrap
`
const TwCheckbox = tw(Checkbox)`shrink-0`

const AccessSettings = observer(() => {
  const [, store] = useStore()
  return (
    <div className="my-16">
      <div className="font-semibold">{Lang.msg.share.accessLabel}:</div>
      <TwRow onClick={() => store.toggleChatAccess()}>
        <TwCheckbox value={store.hasChatAccess} />{' '}
        <TwLabel>{Lang.msg.share.allowCommenting}</TwLabel>
      </TwRow>
      <TwRow onClick={() => store.toggleAddTaskAccess()}>
        <TwCheckbox value={store.hasAddTaskAccess} />{' '}
        <TwLabel>{Lang.msg.share.allowCreateTasks}</TwLabel>
      </TwRow>
      <TwRow onClick={() => store.toggleAllColumnsAccess()}>
        <TwCheckbox value={store.hasAllColumnsAccess} />{' '}
        <TwLabel>{Lang.msg.share.allowAllColumns}</TwLabel>
      </TwRow>
      {!store.hasAllColumnsAccess &&
        [...store.columns.values()].map(col => (
          <TwRow
            key={col.id}
            className="mx-24"
            onClick={() => store.toggleColumn(col.id)}
          >
            <TwCheckbox value={store.columns.get(col.id)?.hasAccess} />{' '}
            <TwLabel>
              {Lang.msg.common.column} “{col.title}”
            </TwLabel>
          </TwRow>
        ))}
    </div>
  )
})

const AddBackground = observer(() => {
  const [, store] = useStore()
  return (
    <DivWithHint
      content={Lang.msg.share.addBg}
      onClick={e => store.addBg(e)}
      className={bem('add-bg')}
    />
  )
})

const ToggleMoreBackgrounds = observer(() => {
  const [state, store] = useStore()
  return (
    <div
      className={bem('more')}
      onClick={() => store.toggleShowAllBackgrounds()}
    >
      {state.showAllBackgrounds ? Lang.msg.showLess : Lang.msg.showMore2 + ' …'}
    </div>
  )
})

const BgSettings = observer(() => {
  const [state, store] = useStore()
  const sharedBoard = store.getSharedBoard()
  const backgrounds = store.getUserBackgrounds()
  const currentBg = sharedBoard?.background ?? backgroundImages[0]
  return (
    <div className={bem('bg')}>
      <div className={bem('bg-title')}>{Lang.msg.share.background}:</div>
      <div className={bem('bg-items')}>
        <AddBackground />
        {backgrounds.slice(0, state.showAllBackgrounds ? 200 : 12).map(bg => (
          <div
            key={bg}
            className={bem('bg-item', {chosen: currentBg === bg})}
            onClick={() => store.change({background: bg})}
            style={{
              backgroundImage: `url(${FilePreview.getBackgroundPreviewUrl(
                bg
              )})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover'
            }}
          />
        ))}
        <ToggleMoreBackgrounds />
      </div>
    </div>
  )
})

const SettingItem = observer(
  (props: {active: boolean; onClick: () => void; label: string}) => (
    <div className={bem('col-item')}>
      <RadioButton
        active={props.active}
        onClick={props.onClick}
        label={props.label}
      />
    </div>
  )
)

const OtherSettings = observer(() => {
  const [, store] = useStore()
  const sharedBoard = store.getSharedBoard()
  return (
    <div className={bem('cols')}>
      <div className={bem('theme-col')}>
        <div className={bem('col-title')}>{Lang.msg.share.theme}:</div>
        <SettingItem
          active={sharedBoard.theme === 'light'}
          onClick={() => store.change({theme: 'light'})}
          label={Lang.msg.share.lightTheme}
        />
        <SettingItem
          active={sharedBoard.theme === 'dark'}
          onClick={() => store.change({theme: 'dark'})}
          label={Lang.msg.share.darkTheme}
        />
      </div>
      <div className={bem('lang-col')}>
        <div className={bem('col-title')}>{Lang.msg.share.lang}:</div>
        <SettingItem
          active={sharedBoard.lang === 'ru'}
          onClick={() => store.change({lang: 'ru'})}
          label={Lang.msg.share.ru}
        />
        <SettingItem
          active={sharedBoard.lang === 'en'}
          onClick={() => store.change({lang: 'en'})}
          label={Lang.msg.share.en}
        />
      </div>
    </div>
  )
})
